import React from "react";
import { Box, List, Text, OrderedList, TableContainer, Table, Thead, Th, Tr, Td, ListItem } from "@chakra-ui/react";
import { type MDXRemoteProps } from "next-mdx-remote";
import { Link } from "ui/link";
import { Video } from "ui/video";
import { PaginatedSlider } from "ui/slider";
import { type EmbeddedWhitepaper } from "lib/sanity";
import { remCalc } from "theme/utils";
import { integrationsCount } from "../../constants/integrations";
import { MdxAccordion } from "./mdx-accordion";
import { MdxImage } from "./mdx-image";
import { MdxCode } from "./mdx-code";
import { DownloadReverseEtlWhitepaper, DownloadWhitepaper } from "./mdx-download-whitepaper";
import { InlineAd } from "./mdx-inline-ad";
import { MdxTestimonial } from "./mdx-testimonial";
import { MdxCompareFeaturesTable } from "./mdx-compare-features-table";
import { MdxProsConsList } from "./mdx-pros-cons-list";

export const components = ({
  type,
  publishedAt,
  allEmbeddedWhitepapers,
}: {
  type: "playbook" | "blog" | "customer" | "adsPage" | "sqlPage" | "cdpComparison";
  publishedAt?: string;
  allEmbeddedWhitepapers?: EmbeddedWhitepaper[];
}): MDXRemoteProps["components"] => ({
  li({ children }) {
    if (type === "adsPage") {
      return <ListItem _notLast={{ mb: "smallest" }}>{children}</ListItem>;
    }

    return <ListItem textStyle="article.listItem">{children}</ListItem>;
  },
  ul({ children }) {
    if (type === "adsPage") {
      return (
        <List listStyleType="disc" mt="small">
          {children}
        </List>
      );
    }

    if (type === "playbook") {
      return (
        <List textStyle="article.list" listStyleType="disc" p="0" mt="0" ml={remCalc(20)} color="brand.text.light">
          {children}
        </List>
      );
    }

    return (
      <List textStyle="article.list" listStyleType="disc">
        {children}
      </List>
    );
  },
  ol({ children }) {
    if (type === "playbook") {
      return (
        <OrderedList textStyle="article.list" p="0" mt="0" ml={remCalc(20)} color="brand.text.light">
          {children}
        </OrderedList>
      );
    }

    return (
      <OrderedList textStyle="article.list" listStyleType="auto">
        {children}
      </OrderedList>
    );
  },
  strong: ({ children }) => (
    <Text as="strong" fontWeight="bold">
      {children}
    </Text>
  ),
  a(props) {
    return <Link variant="inline" fontWeight="bold" target="_blank" {...props} />;
  },
  p({ children }) {
    if (type === "adsPage") {
      return (
        <Text _notFirst={{ mt: "small" }} _notLast={{ mb: "small" }}>
          {children}
        </Text>
      );
    }

    return (
      <Text textStyle="article.p" color="brand.text.light">
        {children}
      </Text>
    );
  },
  h1: ({ children, id }) => (
    <Text as="h1" textStyle={{ base: "article.h2", md: "article.h1" }} id={id} className="mdx-heading">
      {children}
    </Text>
  ),
  h2({ children, id }) {
    const isPlaybook = type === "playbook";

    return (
      <Text
        as="h2"
        id={id}
        className="mdx-heading"
        scrollMarginTop={32}
        textStyle={isPlaybook ? { base: "article.h5", md: "article.h4" } : { base: "article.h3", md: "article.h2" }}
        textAlign={type === "adsPage" ? "center" : undefined}
        my={isPlaybook ? { base: "smaller", md: "smaller" } : undefined}
      >
        {children}
      </Text>
    );
  },
  h3: ({ children, id }) => (
    <Text
      as="h3"
      textStyle={{ base: "article.h4", md: "article.h3" }}
      id={id}
      className="mdx-heading"
      textAlign={type === "adsPage" ? "center" : undefined}
      scrollMarginTop={32}
    >
      {children}
    </Text>
  ),
  h4: ({ children, id }) => (
    <Text
      as="h4"
      textStyle="article.h4"
      id={id}
      className="mdx-heading"
      scrollMarginTop={32}
      textAlign={type === "adsPage" ? "center" : undefined}
    >
      {children}
    </Text>
  ),
  blockquote: ({ children }) => (
    <Text as="blockquote" fontStyle="italic" my={8} borderLeft="6px solid #eee" pl={3}>
      {children}
    </Text>
  ),
  img({ src, alt }) {
    return <MdxImage src={src} alt={alt} />;
  },
  pre: ({ className, children }) => (
    <Box
      as="pre"
      className={className}
      borderRadius="base"
      bg="brand.backgroundSecondary.light"
      color="new.gray.900"
      fontSize="sm"
      p={{ base: 4, md: 8 }}
      my={8}
      w={{ base: "100%", md: "container.article", xl: "100%" }}
      maxW="100%"
    >
      {children}
    </Box>
  ),
  code: ({ className, children }) => <MdxCode className={className}>{children}</MdxCode>,
  table: ({ className, children }) => (
    <TableContainer className={className} my={8} maxW="100%" overflowX="auto" whiteSpace="break-spaces">
      <Table variant="simple">{children}</Table>
    </TableContainer>
  ),
  thead: ({ className, children }) => <Thead className={className}>{children}</Thead>,
  th: ({ className, children }) => (
    <Th className={className} fontFamily="inter">
      {children}
    </Th>
  ),
  tr: ({ className, children }) => <Tr className={className}>{children}</Tr>,
  td: ({ className, children }) => (
    <Td textStyle="caption1" className={className}>
      {children}
    </Td>
  ),
  section({ children }) {
    if (type === "playbook" && children) {
      return <MdxAccordion>{children}</MdxAccordion>;
    }

    return <Box>{children}</Box>;
  },
  DownloadReverseETLWhitepaper: () => <DownloadReverseEtlWhitepaper />,
  DownloadWhitepaper(props) {
    const { slug, variant } = props;
    const embeddedWhitepaper = allEmbeddedWhitepapers?.find((w) => w.slug === slug);
    const variantContent = variant === "long" ? embeddedWhitepaper?.longVariant : embeddedWhitepaper?.shortVariant;

    if (!embeddedWhitepaper || !variantContent) {
      return null;
    }

    const { title, fileName, fileUrl, posterImageUrl, hubSpotFormId } = embeddedWhitepaper;

    return (
      <DownloadWhitepaper
        variant={variant}
        title={title}
        fileName={fileName}
        fileUrl={fileUrl}
        posterImageUrl={posterImageUrl}
        hubSpotFormId={hubSpotFormId}
        variantContent={variantContent}
      />
    );
  },
  InlineAd(props) {
    return (
      <InlineAd
        title={props.title}
        description={props.description}
        ctaText={props.ctaText}
        ctaLink={props.ctaLink}
        imageUrl={props.imageUrl}
        imageAlt={props.imageAlt}
      />
    );
  },
  QuoteSlider: ({ children }) => (
    <Box
      className="quote-slider"
      borderRadius="base"
      bg="new.blue.900"
      my={16}
      w={{ base: "100%", md: "container.article" }}
      maxW="100%"
    >
      <PaginatedSlider isBlog theme="dark" slides={children} display="flex" justifyContent="center" />
    </Box>
  ),
  Quote(props) {
    return (
      <Box
        sx={{
          my: 16,
          // Minimize vertical margins if it's within a slider
          ".quote-slider &": {
            my: { base: 4, lg: 2 },
          },
        }}
      >
        <MdxTestimonial
          author={props.author}
          quote={props.content}
          job={props.title}
          company={props.company}
          image={props.image}
        />
      </Box>
    );
  },
  Video(props) {
    return (
      <Video
        type="upload"
        poster={props.poster}
        source={props.source}
        duration={props.duration}
        title={props.title}
        description={`${props.title}. See it on the Hightouch blog.`}
        uploadDate={publishedAt}
        mb={12}
      />
    );
  },
  YoutubeVideo(props) {
    return (
      <Video
        type="youtube"
        poster={props.poster}
        source={props.source}
        duration={props.duration}
        title={props.title}
        description={props.description ?? `${props.title}. See it on the Hightouch blog.`}
        uploadDate={props.uploadDate ?? publishedAt}
        mb={12}
      />
    );
  },
  Footer({ children }) {
    return <Box textStyle="article.footer">{children}</Box>;
  },
  CompareFeaturesTable(props) {
    return <MdxCompareFeaturesTable csvContent={props.csvContent} />;
  },
  ProsConsList(props) {
    return <MdxProsConsList {...props} />;
  },
  IntegrationsCount() {
    // Include as a fragment to keep types happy, without introducing unnecessary spans
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{integrationsCount}</>;
  },
  ImageSmall({ src, alt }) {
    return <MdxImage size="small" src={src} alt={alt} />;
  },
  SectionLink({ id, children }) {
    return (
      <Link variant="inline" fontWeight="bold" href={`#${id}`}>
        {children}
      </Link>
    );
  },
});
